import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { fmtMoney, fmtKW } from "@/lib/format";
import { dealStageMap } from "@/components/StatusBadge";
import {
  Users2,
  Kanban,
  Hammer,
  Building2,
  TrendingUp,
  Zap,
  Wallet,
  AlertCircle,
  Sun,
} from "lucide-react";
import { Link } from "react-router-dom";
import { fmtDate } from "@/lib/format";

const STAGE_ORDER = ["new", "qualified", "site_survey", "proposal", "negotiation", "won", "lost"];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => {
      setStats(r.data);
      setLoading(false);
    });
  }, []);

  if (loading) return <div className="text-slate-500">Loading dashboard…</div>;
  const s = stats;

  const maxStage = Math.max(
    1,
    ...STAGE_ORDER.map((k) => s.pipeline_by_stage?.[k]?.count || 0)
  );

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title="Dashboard"
        subtitle="Snapshot of your solar EPC pipeline, projects and cash flow."
        testid="dashboard-header"
      />

      {/* Hero KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi
          icon={<TrendingUp className="w-5 h-5" />}
          label="Weighted pipeline"
          value={fmtMoney(s.money.weighted_pipeline)}
          hint={`Total ${fmtMoney(s.money.pipeline_value)}`}
          tint="from-[#0F3A66] to-[#164B82]"
          tid="kpi-weighted-pipeline"
        />
        <Kpi
          icon={<Zap className="w-5 h-5" />}
          label="Installed capacity"
          value={fmtKW(s.capacity.installed_kw)}
          hint={`Won: ${fmtKW(s.capacity.won_capacity_kw)}`}
          tint="from-[#10B981] to-[#059669]"
          tid="kpi-installed"
        />
        <Kpi
          icon={<Wallet className="w-5 h-5" />}
          label="Collected"
          value={fmtMoney(s.money.total_collected)}
          hint={`Invoiced ${fmtMoney(s.money.total_invoiced)}`}
          tint="from-[#0EA5E9] to-[#0369A1]"
          tid="kpi-collected"
        />
        <Kpi
          icon={<AlertCircle className="w-5 h-5" />}
          label="Outstanding"
          value={fmtMoney(s.money.outstanding)}
          hint="Pending collections"
          tint="from-[#F59E0B] to-[#B45309]"
          tid="kpi-outstanding"
        />
      </div>

      {/* Secondary counts */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        <CountCard icon={Users2} label="Leads" value={s.counts.leads_total} sub={`${s.counts.leads_new} new`} to="/leads" tid="count-leads" />
        <CountCard icon={Kanban} label="Active deals" value={s.counts.active_deals} sub={`${s.counts.won_deals} won`} to="/deals" tid="count-deals" />
        <CountCard icon={Hammer} label="Active projects" value={s.counts.active_projects} sub={`${s.counts.completed_projects} done`} to="/projects" tid="count-projects" />
        <CountCard icon={Building2} label="Customers" value={s.counts.customers_total} sub="Accounts" to="/customers" tid="count-customers" />
      </div>

      {/* Pipeline funnel & follow-ups */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <div className="crm-card p-6 lg:col-span-2" data-testid="pipeline-funnel">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display text-lg font-semibold text-slate-900">Pipeline by stage</h3>
              <p className="text-xs text-slate-500">Deal count and value per stage</p>
            </div>
            <Link to="/deals" className="btn-ghost text-sm">Open Kanban →</Link>
          </div>
          <div className="space-y-3">
            {STAGE_ORDER.map((k) => {
              const cell = s.pipeline_by_stage?.[k] || { count: 0, value: 0 };
              const pct = Math.round((cell.count / maxStage) * 100);
              const cfg = dealStageMap[k];
              return (
                <div key={k} className="flex items-center gap-3">
                  <div className="w-32 text-sm text-slate-600">{cfg.label}</div>
                  <div className="flex-1 bg-slate-100 rounded-lg h-8 overflow-hidden relative">
                    <div
                      className="h-full rounded-lg transition-all"
                      style={{
                        width: `${pct}%`,
                        background: `linear-gradient(90deg, ${cfg.text}22, ${cfg.text})`,
                      }}
                    />
                    <div className="absolute inset-0 flex items-center px-3 justify-between">
                      <span className="text-xs font-semibold text-slate-800">{cell.count}</span>
                      <span className="text-xs font-medium text-slate-700">{fmtMoney(cell.value)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="crm-card p-6" data-testid="upcoming-followups">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display text-lg font-semibold text-slate-900">Upcoming follow-ups</h3>
              <p className="text-xs text-slate-500">Next 7 days</p>
            </div>
            <Sun className="w-5 h-5 text-[#F59E0B]" />
          </div>
          {s.upcoming_followups.length === 0 && (
            <div className="text-sm text-slate-500 py-8 text-center border border-dashed rounded-lg">
              No upcoming follow-ups. You&apos;re all clear!
            </div>
          )}
          <ul className="space-y-2">
            {s.upcoming_followups.map((f) => (
              <li key={f.id} className="p-3 rounded-lg border border-slate-200 hover:border-[#10B981] transition-colors">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium text-slate-800 truncate">{f.title}</div>
                  <span className="badge bg-slate-100 text-slate-600">{f.type}</span>
                </div>
                <div className="text-xs text-slate-500 mt-1">Due: {fmtDate(f.due_date)}</div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon, label, value, hint, tint, tid }) {
  return (
    <div className={`relative rounded-2xl p-5 text-white bg-gradient-to-br ${tint} overflow-hidden`} data-testid={tid}>
      <div className="absolute -right-4 -top-4 w-24 h-24 rounded-full bg-white/10" />
      <div className="relative flex items-center justify-between">
        <div className="w-10 h-10 rounded-lg bg-white/15 flex items-center justify-center">{icon}</div>
      </div>
      <div className="relative mt-6">
        <div className="text-xs uppercase tracking-widest text-white/70">{label}</div>
        <div className="font-display text-2xl md:text-3xl font-semibold mt-1">{value}</div>
        <div className="text-xs text-white/70 mt-1">{hint}</div>
      </div>
    </div>
  );
}

function CountCard({ icon: Icon, label, value, sub, to, tid }) {
  return (
    <Link
      to={to}
      className="crm-card p-5 flex items-center gap-4"
      data-testid={tid}
    >
      <div className="w-11 h-11 rounded-lg bg-[#10B981]/10 text-[#059669] flex items-center justify-center">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
        <div className="font-display text-2xl font-semibold text-slate-900">{value}</div>
        <div className="text-xs text-slate-500">{sub}</div>
      </div>
    </Link>
  );
}
